import * as React from "react";
import {connect} from "react-redux";
import SerialNumbersData from "../../../../../model/job/SerialNumbersData";
import EditWarrantyInfoFormView from "./EditWarrantyInfoFormView";
import {none, option, Option} from "ts-option";
import {Dispatch} from "redux";
import {OnFailure, OnSuccess} from "../../../../../store/action/actionHelpers";
import Job from "../../../../../model/job/Job";
import {downloadWarrantyCsv, editJob} from "../../../../../store/action/job/job";
import Toast from "../../../../helper/Toast";
import IndividualContact from "../../../../../model/contact/IndividualContact";
import {RootState} from "../../../../../store/reducer/root/RootState";
import {getIndividualContact} from "../../../../../store/view/contact";
import WarrantyInfoData from "../../WarrantyInfoData";
import {editIndividualContact} from "../../../../../store/action/contacts/contacts";
import {notifyBugsnagAndShowToast} from "../../../../../utils/Utils";

interface IComponentProps {
	/**
	 * On successful callback.
	 */
	onSuccess: () => void;
	/**
	 * On cancel button click.
	 */
	onCancel: () => void;
	/**
	 * Job to edit.
	 */
	job: Option<Job>;
}

interface ReduxProps {
	/**
	 * Contact
	 */
	contact: Option<IndividualContact>;
}

interface IDispatchProps {
	/**
	 * Update job data.
	 * @param {job} Job
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateJob: (job: Job, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Update contact data.
	 * @param {contact} IndividualContact
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	editIndividualContact: (contact: IndividualContact, onSuccess: OnSuccess, onFailure: OnFailure) => void
}

interface IState {
	/**
	 * Indicate if any server action is in progress.
	 */
	isSubmitting: boolean;
}

type Props = IComponentProps & ReduxProps & IDispatchProps;

class EditWarrantyInfoForm extends React.PureComponent<Props, IState> {
	constructor(props: Props) {
		super(props);
		this.state = {
			isSubmitting: false
		};
		this.onUpdateData = this.onUpdateData.bind(this);
		this.onSubmitFailure = this.onSubmitFailure.bind(this);
		this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
	}

	render() {
		return (
			<EditWarrantyInfoFormView onSubmit={this.onUpdateData}
									  onCancel={this.props.onCancel}
									  onExportCSV={this.downloadWarranty}
									  isSubmitting={this.state.isSubmitting}
									  serialNumbers={this.props.job.flatMap(j => j.data.serialNumbers)}
									  title={this.props.contact.flatMap(c => c.data.mainData.title)}
									  merchantId={this.props.job.flatMap(j => j.data.merchantId)}
									  isJobWithQuote={this.props.job.map(j => j.quoteId.isDefined).getOrElse(false)}/>
		);
	}

	onUpdateData(editedData: WarrantyInfoData) {
		this.setState({isSubmitting: true}, () => {
			this.props.job.map(j => {
				const updatedJob = j.copy({
					data: j.data.copy({
						serialNumbers: option(
							new SerialNumbersData(editedData.boiler, editedData.cylinder, editedData.filter,
								editedData.control)),
						merchantId: editedData.merchantId
					})
				});
				this.props.updateJob(
					updatedJob,
					() => {
						this.props.contact.map(c => {
							const updatedContact = c.copy(
								{data: c.data.copy({mainData: c.data.mainData.copy({title: editedData.title})})});
							this.props.editIndividualContact(
								updatedContact,
								this.onSubmitSuccess,
								(err) => this.onSubmitFailure(err, 'Warranty Info Contact update')
							);
						});
					},
					(err) => this.onSubmitFailure(err, 'Warranty Info Job update')
				);
			});
		});
	}

	onSubmitSuccess() {
		this.setState({isSubmitting: false}, () => {
			Toast.success("Warranty Info updated", "Warranty Info update");
			this.props.onSuccess();
		});
	}

	onSubmitFailure(err: any, title: string) {
		this.setState({isSubmitting: false}, () => {
			notifyBugsnagAndShowToast(err, title);
		});
	}

	downloadWarranty = () => this.props.job.map(job => downloadWarrantyCsv(job.id))
}

export enum WarrantyDownloadErrors {
	SingleWarrantyCodeRequired = "SINGLE_WARRANTY_CODE_REQUIRED",
	JobNotCompleted = "JOB_NOT_COMPLETED",
	MissingBoilerSerialNumber = "MISSING_BOILER_SERIAL_NUMBER",
	MissingPostCode = "MISSING_POST_CODE",
	MissingHouseIdentifier = "MISSING_HOUSE_IDENTIFIER",
	MissingStreet = "MISSING_STREET",
	MissingCity = "MISSING_CITY",
	MissingCustomerTitle = "MISSING_CUSTOMER_TITLE",
	NoWorcesterAssetInJob = "NO_WORCESTER_ASSET_IN_JOB"
}


function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateJob: (job: Job, onSuccess: OnSuccess, onFailure: OnFailure) => {
			dispatch(editJob(job, onSuccess, onFailure));
		},
		editIndividualContact: (contact: IndividualContact, onSuccess: OnSuccess, onFailure: OnFailure) => {
			dispatch(editIndividualContact(contact, none, false, onSuccess, onFailure))
		}
	};
}

function mapStateToProps(state: RootState, props: IComponentProps) {
	return {
		contact: props.job.flatMap(j => getIndividualContact(j.data.principalContactId, state))
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(EditWarrantyInfoForm);